"use client";

import { AuthUserParams } from "api/auth/types";
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { Chain } from "viem";
import { useAccount, useDisconnect, useSignMessage } from "wagmi";
import { login, logout, setAuthentication, setUser } from "store/auth/auth.slice";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { User } from "api/users/types";
import { verifyMessage } from "@wagmi/core";
import { WagmiConfig } from "./WagmiConfig";
import { fetchEthPriceUsd } from "store/coingecko/prices.slice";
import {
  CrossmintProvider,
  CrossmintAuthProvider,
  useAuth as useCrossmintAuth
} from "@crossmint/client-sdk-react-ui";
import { UserType } from "api/users/enums";

interface AppContextType {
  authUser: User | null;
  address: string | undefined;
  chain: Chain | undefined;
  isWalletConnected: boolean;
  isWalletConnecting: boolean;
  isAuthenticated: boolean;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  logoutApp: () => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppContextProvider({ children }: { children: ReactNode }) {
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);

  // Wagmi
  const { chain, address, isConnected, status, isConnecting } = useAccount();
  const { disconnectAsync } = useDisconnect();
  const { signMessageAsync } = useSignMessage();

  // Crossmint
  const crossmintApiKey =
    process.env.REACT_APP_CROSSMINT_CLIENT_API_KEY ??
    process.env.NEXT_PUBLIC_CROSSMINT_API_KEY ??
    "";

  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isWalletConnected =
    isConnected &&
    !isConnecting &&
    status === "connected" &&
    Boolean(chain) &&
    Boolean(address) &&
    !isLoggingOut;

  const isWalletConnecting =
    isConnecting || ["reconnecting", "connecting"].includes(status);

  const logoutApp = useCallback(async () => {
    setIsLoggingOut(true);
    await disconnectAsync();
    await dispatch(logout());
    setIsLoggingOut(false);
  }, [disconnectAsync, dispatch]);

  // -------------------------------
  // Wagmi authenticate (signature)
  // -------------------------------
  const authenticate = useCallback(async () => {
    setLoading(true);

    if (!isWalletConnected || isAuthenticated) {
      setLoading(false);
      return;
    }

    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser) as User;
      dispatch(setAuthentication(true));
      dispatch(setUser(parsedUser));
      setLoading(false);
      return;
    }

    try {
      const nonce: string = uuidv4();
      const addressFormatted: string = (address as string).toLowerCase();
      const messageTemplate = t("auth.messages.signature");
      const message: string = messageTemplate
        .replace("{address}", addressFormatted)
        .replace("{nonce}", nonce);

      const signature = await signMessageAsync({ message });

      const isValid = await verifyMessage(WagmiConfig, {
        address,
        message,
        signature
      });

      if (!isValid) {
        throw new Error("Invalid signature");
      }

      const params: AuthUserParams = {
        user_fields: "wallets,token",
        address: addressFormatted,
        signature,
        nonce,
        message,
        chain: chain?.name,
        email: localStorage.getItem("@appkit-wallet/EMAIL") || ""
      };

      await dispatch(login(params));
    } catch {
      enqueueSnackbar(t("auth.errors.signature_failed"), {
        variant: "warning"
      });
      await logoutApp();
    }

    setLoading(false);
  }, [
    isWalletConnected,
    isAuthenticated,
    address,
    chain,
    signMessageAsync,
    enqueueSnackbar,
    t,
    dispatch,
    logoutApp
  ]);

  useEffect(() => {
    if (isLoggingOut) {
      return;
    }

    setLoading(true);

    if (isWalletConnected && !isAuthenticated) {
      void authenticate();
    }

    setLoading(false);
  }, [isWalletConnected, isAuthenticated, authenticate, isLoggingOut]);

  // -------------------------------
  // Fetch ETH price auto-refresh
  // -------------------------------
  useEffect(() => {
    dispatch(fetchEthPriceUsd());

    const interval = setInterval(() => {
      dispatch(fetchEthPriceUsd());
    }, 60000);

    return () => clearInterval(interval);
  }, [dispatch]);

  // -----------------------------------------
  // CROSSMINT AUTH → SYNCHRONIZE WITH REDUX
  // -----------------------------------------
  function CrossmintAuthSync({ children }: { children: ReactNode }) {
    const { user: cmUser, jwt } = useCrossmintAuth();

    useEffect(() => {
      if (cmUser && !isAuthenticated) {
        const mappedUser: User = {
          id: 1,
          username: '0x0fA522E9ab6aD103924A11f788B69860a0189f89',
          email: cmUser.email ?? "",
          name: cmUser.email ?? "",
          profile_photo_url: '',

          // Crossmint wallets → your structure
          wallets: [],

          // REQUIRED FIELDS FROM YOUR User TYPE:
          type: UserType.User,
          banner_image_permalink: '',
          short_description: "",
          bio: '',
        };

        dispatch(setUser(mappedUser));
        dispatch(setAuthentication(true));
      }

      if (!cmUser && isAuthenticated) {
        dispatch(logout());
      }
    }, [cmUser, jwt, isAuthenticated, dispatch]);

    return <>{children}</>;
  }

  return (
    <CrossmintProvider apiKey={crossmintApiKey}>
      <CrossmintAuthProvider loginMethods={["email", "google", "web3"]}>
        <CrossmintAuthSync>
          <AppContext.Provider
            value={{
              isWalletConnected,
              isWalletConnecting,
              isAuthenticated,
              authUser: user,
              address,
              chain,
              loading,
              setLoading,
              error,
              setError,
              logoutApp
            }}
          >
            {children}
          </AppContext.Provider>
        </CrossmintAuthSync>
      </CrossmintAuthProvider>
    </CrossmintProvider>
  );
}

export function useAppContext() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within a AppContextProvider");
  }
  return context;
}
